// Network: the snapshots a server sends its clients, and their application on the client side.
//
// A snapshot has a hot part, sent every time (six numbers per rider: position, lane, speed, watts, cadence, shelter,
// the ones the client interpolates), and a cold part (gear, item, effects, shield, elimination, finish, scores…) sent
// only for the riders whose cold state changed since the stream's previous snapshot. Effects and the shield travel
// as their end time in race seconds, not as seconds left, so a running effect does not change the cold state every
// tick. Every FULL_EVERY snapshots, and after a client resumes, the cold part carries every rider: a client that
// missed snapshots is whole again within two seconds. The binary form is in race-net-binary.ts.
import type { ItemKey } from './items.js';
import type { ColdState, Rider, Snapshot } from './race-types.js';
import type { Race } from './race.js';

/** Snapshots between two full ones: 2 s at 15 Hz. */
export const FULL_EVERY = 30;

/** Hot state of a rider: what moves every tick. */
export function hotOf(r: Rider): number[] {
  return [+r.s.toFixed(2), +r.lane.toFixed(3), +r.v.toFixed(2), Math.round(r.power), Math.round(r.cadence), +r.draft.toFixed(2)];
}

/** Cold state of a rider at race time `t`: what changes on events only. */
export function coldOf(r: Rider, t: number): ColdState {
  const fx: Record<string, number> = {};
  for (const [k, v] of Object.entries(r.fx)) if (v != null && v > 0) fx[k] = +(t + v).toFixed(2);
  // the shield's end is null, not 0, when there is none: race time is negative during the countdown
  return [r.gear, r.item, r.itemRoll > 0 ? 1 : 0, fx, r.shield > 0 ? +(t + r.shield).toFixed(1) : null, r.invuln > 0 ? 1 : 0, r.out ? 1 : 0, r.finishT, r.hits, r.outOrder, r.human ? 1 : 0, +r.bombMult.toFixed(3)];
}

/**
 * One stream of snapshots per race: remembers the cold state last sent for each rider and sends it again only when
 * it changes, or in a full snapshot (the first one, then every FULL_EVERY, or on demand).
 */
export class SnapshotStream {
  private cold: string[] = [];
  private since = Infinity;
  /** The next snapshot carries every rider's cold state (a client resumed after a drop). */
  forceFull() { this.since = Infinity; }
  next(race: Race): Snapshot {
    const full = this.since >= FULL_EVERY, c: Record<number, ColdState> = {};
    race.riders.forEach((r, i) => {
      const cs = coldOf(r, race.t), key = JSON.stringify(cs);
      if (full || key !== this.cold[i]) { c[i] = cs; this.cold[i] = key; }
    });
    this.since = full ? 1 : this.since + 1;
    return {
      t: +race.t.toFixed(3), st: race.state, el: race.elimLap, ...(full ? { f: 1 as const } : {}),
      r: race.riders.map(hotOf), c,
      h: race.hazards.map(z => (z.item ? [+z.s.toFixed(1), z.lane, z.item] : [+z.s.toFixed(1), z.lane]) as [number, number, string?]),
      sh: race.shells.map(s => (s.item ? [+s.s.toFixed(1), +s.lane.toFixed(2), s.item] : [+s.s.toFixed(1), +s.lane.toFixed(2)]) as [number, number, string?]),
      b: race.boxes.map(b => (b.cd > 0 ? 1 : 0)),
    };
  }
}

/** A full snapshot on its own (the first of a fresh stream): tools and tests. */
export function snapshot(race: Race): Snapshot { return new SnapshotStream().next(race); }

/** Seconds left of each effect and of the shield at race time `t`, from the end times received. */
function derive(r: Rider, t: number) {
  if (r.fxEnd) { const fx: Rider['fx'] = {}; for (const [k, end] of Object.entries(r.fxEnd)) if (end - t > 0) fx[k] = end - t; r.fx = fx; }
  if (r.shieldEnd !== undefined) r.shield = r.shieldEnd == null ? 0 : Math.max(0, r.shieldEnd - t);
}

/** Applies the cold part: the riders present in `S.c`. Done on receipt of every snapshot, so none is missed. */
export function applyCold(race: Race, S: Snapshot) {
  for (const [k, d] of Object.entries(S.c)) {
    const r = race.riders[+k]; if (!r) continue;
    [r.gear, r.item] = d;
    r.itemRoll = d[2] ? 1 : 0; r.fxEnd = d[3]; r.shieldEnd = d[4]; r.invuln = d[5] ? 1 : 0; r.out = !!d[6]; r.finishT = d[7];
    r.hits = d[8]; r.outOrder = d[9]; r.human = !!d[10]; r.bombMult = d[11];
    derive(r, S.t);
  }
}

/** Applies the hot part, interpolated between `prev` and `S` by `a` when `prev` is given: positions at render time. */
export function applyHot(race: Race, S: Snapshot, a = 1, prev: Snapshot | null = null) {
  const lerp = (x: number, y: number) => (prev ? x + (y - x) * a : y);
  race.t = prev ? lerp(prev.t, S.t) : S.t; race.state = S.st; race.elimLap = S.el;
  S.r.forEach((d, i) => {
    const r = race.riders[i]; if (!r) return;
    const p = prev && prev.r[i];
    r.prevS = r.s;
    r.s = p ? lerp(p[0], d[0]) : d[0]; r.lane = p ? lerp(p[1], d[1]) : d[1]; r.v = p ? lerp(p[2], d[2]) : d[2];
    r.power = d[3]; r.cadence = d[4]; r.draft = d[5];
    derive(r, race.t);
  });
  race.hazards = S.h.map(([s, lane, item]) => ({ s, lane, age: 0, ...(item ? { item: item as ItemKey } : {}) }));
  race.shells = S.sh.map(([s, lane, item], i) => { const p = prev && prev.sh[i]; return { s: p ? lerp(p[0], s) : s, lane: p ? lerp(p[1], lane) : lane, target: null, life: 1, dist: 0, ...(item ? { item: item as ItemKey } : {}) }; });
  S.b.forEach((c, i) => { if (race.boxes[i]) race.boxes[i].cd = c ? 1 : 0; });
}

/** Both parts of one snapshot. A client applies the cold part on receipt and the hot one at render time (NetRace). */
export function applySnapshot(race: Race, S: Snapshot, a = 1, prev: Snapshot | null = null) {
  applyCold(race, S);
  applyHot(race, S, a, prev);
}
